import React, { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { Row, Col, Tag, Dropdown, Card, Input, Button, List, Checkbox } from 'antd';
import SearchOutlined from '@ant-design/icons/lib/icons/SearchOutlined';

import { ComponentSpinner } from '../../../../shared/ComponentSpinner';

import { ReactComponent as KeyboardArrowDown } from '../../../../../images/keyboard_arrow_down.svg';
import { useGetSearchResultsLazyQuery } from '../../../../../graphql/search.generated';
import { AndFilterInput, Entity, EntityType } from '../../../../../types.generated';
import { useGetRecommendations } from '../../../../shared/recommendation';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../../useEntityRegistry';

type Props = {
    // Label shown next to the selected count e.g. "Domain", "Owner"
    title: string;
    // Entity type used to drive recommendations and search e.g. EntityType.Domain, EntityType.CorpUser
    entityType: EntityType;
    // Optional orFilters applied to both recommendations and search (e.g. active-user filter for Owners)
    filter?: AndFilterInput[];
    // Notifies the parent of the applied selection so it can drive the dashboard filters
    onChange?: (urns: string[]) => void;
};

const TOP_RESULTS_COUNT = 5;

const TopSelector = ({ title, entityType, filter, onChange }: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();

    const [open, setOpen] = useState(false);
    // Raw value bound to the Input — updates on every keystroke so the field stays responsive
    const [inputValue, setInputValue] = useState('');
    // Debounced copy used to drive the API call and decide which option list to show
    const [searchQuery, setSearchQuery] = useState('');
    // Selection committed via "Apply" and reflected in the trigger tag
    const [appliedUrns, setAppliedUrns] = useState<string[]>([]);
    // Selection being edited while the dropdown is open
    const [draftUrns, setDraftUrns] = useState<string[]>([]);

    const [recommendations, recommendationsLoading] = useGetRecommendations([entityType], filter);
    const [search, { data: searchData, loading: searchLoading }] = useGetSearchResultsLazyQuery();

    const searchResults = useMemo(
        () => searchData?.search?.searchResults?.map((result) => result.entity) || [],
        [searchData],
    );

    // Show recommendations when there is no active search, otherwise the search results
    const options: Entity[] = searchQuery ? searchResults : recommendations;
    // Reflects the in-progress call for whichever source is currently feeding the list
    const optionsLoading = searchQuery ? searchLoading : recommendationsLoading;

    useDebounce(
        () => {
            setSearchQuery(inputValue);
            if (inputValue) {
                search({
                    variables: {
                        input: {
                            type: entityType,
                            query: inputValue,
                            start: 0,
                            count: TOP_RESULTS_COUNT,
                            ...(filter && { orFilters: filter }),
                        },
                    },
                });
            }
        },
        300,
        [inputValue],
    );

    const toggleUrn = (urn: string, checked: boolean) => {
        setDraftUrns((prev) => (checked ? [...prev, urn] : prev.filter((value) => value !== urn)));
    };

    const onDeselectAll = () => {
        setDraftUrns([]);
    };

    const onApply = () => {
        setAppliedUrns(draftUrns);
        onChange?.(draftUrns);
        setOpen(false);
    };

    const onOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) {
            // Sync the draft with the applied selection and reset the search on every open
            setDraftUrns(appliedUrns);
            setInputValue('');
            setSearchQuery('');
        }
    };

    return (
        <Dropdown
            trigger={['click']}
            visible={open}
            onVisibleChange={onOpenChange}
            overlay={
                <Card className="c-top-selector-dropdown f-bordered">
                    <div className="pa-2">
                        <Input
                            allowClear
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={t('Search {{title}}', { title })}
                            prefix={<SearchOutlined />}
                        />
                    </div>
                    {optionsLoading && <ComponentSpinner />}
                    <Row gutter={16} align="middle" className="ma-0">
                        <Col flex="auto">
                            <div className="f-text-content f-text-single-line f-color-dark-black-s70 f-text-uppercase">
                                {title}
                            </div>
                        </Col>
                        <Col flex="none">
                            <Button
                                type="link"
                                className="f-btn-link"
                                disabled={!draftUrns.length}
                                onClick={onDeselectAll}
                            >
                                {t('De-select All')}
                            </Button>
                        </Col>
                    </Row>
                    <List>
                        {options.map((entity) => {
                            const checked = draftUrns.includes(entity.urn);
                            return (
                                <List.Item key={entity.urn} className={checked ? 'f-active-list-item' : ''}>
                                    <Checkbox
                                        checked={checked}
                                        onChange={(e) => toggleUrn(entity.urn, e.target.checked)}
                                    >
                                        {entityRegistry.getDisplayName(entity.type, entity)}
                                    </Checkbox>
                                </List.Item>
                            );
                        })}
                    </List>
                    <div className="pa-2">
                        <Button block type="primary" className="f-btn-pri" disabled={optionsLoading} onClick={onApply}>
                            {t('Apply')}
                        </Button>
                    </div>
                </Card>
            }
        >
            <Row align="middle" className="c-top-selector">
                <Col flex="none">
                    <div className="f-text-medium-content f-text-single-line f-color-dark-black-s100">{title}</div>
                </Col>
                <Col flex="none">
                    <Tag className="label-tag base-tag ma-0">{appliedUrns.length ? appliedUrns.length : t('All')}</Tag>
                </Col>
                <Col flex="none">
                    <div className="ant-row ant-row-middle">
                        <KeyboardArrowDown aria-hidden="true" className="f-icon-size-xs f-color-dark-black-s70" />
                    </div>
                </Col>
            </Row>
        </Dropdown>
    );
};

export default TopSelector;
